import React, { ClipboardEventHandler, createRef, FormEventHandler } from 'react'

import cx from 'classnames'

import { useAppSelector } from '@/utils/useAppSelector'

import styles from './PinInput.module.scss'

const isInputEvent = (event: Event): event is InputEvent => event.type === 'input'

interface PinInputProps {
  pin: string[]
  onChange: (pin: string[]) => void
  onKeyPress: (e: React.KeyboardEvent) => void
}
export const PinInput: React.FC<PinInputProps> = ({ pin, onChange, onKeyPress }) => {
  const error = useAppSelector((state) => state.user.error)
  const pinRefs = pin.map(() => createRef<HTMLInputElement>())

  const handler =
    (idx: number): FormEventHandler<HTMLInputElement> =>
    (event) => {
      event.preventDefault()
      const nativeEvent = event.nativeEvent
      if (isInputEvent(nativeEvent)) {
        const newPin = [...pin]
        if (nativeEvent.inputType === 'insertText') {
          const value = nativeEvent.data
          switch (value) {
            case '0':
            case '1':
            case '2':
            case '3':
            case '4':
            case '5':
            case '6':
            case '7':
            case '8':
            case '9':
              break
            default:
              return
          }
          newPin[idx] = value
          if (idx + 1 < 6) {
            pinRefs[idx + 1].current?.focus()
          }
        } else if (
          nativeEvent.inputType === 'deleteContentBackward' ||
          nativeEvent.inputType === 'deleteContentForward'
        ) {
          newPin[idx] = ''
          if (idx > 0 && nativeEvent.inputType === 'deleteContentBackward') {
            pinRefs[idx - 1].current?.focus()
          }
        }
        onChange(newPin)
      }
    }

  // Handle backspace also via key up as the onInput is not triggered if the input field is empty
  const keyUpHandler = (value: string, idx: number) => {
    if (idx > 0 && value === 'Backspace' && pin[idx] === '') {
      pinRefs[idx - 1].current?.focus()
    }
  }

  const pastePinHandler: ClipboardEventHandler<HTMLInputElement> = (event) => {
    const newPin = event.clipboardData.getData('text').replace(' ', '').slice(0, 6).split('')
    if (newPin.length === 6) {
      onChange(newPin)
    }
  }

  return (
    <div className={cx(styles.PinContainer, { [styles.Error]: error })} data-cy="pin-input-container">
      {pin.map((_, idx) => {
        return (
          <input
            key={idx}
            type="tel"
            ref={pinRefs[idx]}
            value={pin[idx]}
            onKeyPress={onKeyPress}
            autoFocus={idx === 0}
            onPaste={pastePinHandler}
            autoComplete="off"
            onInput={handler(idx)}
            onKeyUp={(e) => {
              keyUpHandler(e.key, idx)
            }}
          />
        )
      })}
    </div>
  )
}
